/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import axios from 'axios';
import { toast } from 'sonner';
import { reportError } from '@/shared/ui/components/basic/error-notification/report-error';
import type { CatalogPublication } from '@/features/publications-catalog/types';
import { boostErrorCode, boostErrorDetail, buildAdSpendConsent } from '@/shared/utils/ad-dialog-helpers';
import { portalsAvailableToPublish } from '@/shared/utils/portal-publish';
import type { AdErrorInfo } from '../ad-dialogs/ad-dialog-types';
import { API_BASE, buildReadHeaders, withAntiBotRetry } from './api';
import type { PortalPublication, PublicationRow } from './types';
import { assertPortalAccepted, refusalMessage } from './portal-refusal';
import { adReadinessFrom, savedBudgetCents, savedDurationDays, type AdReadiness } from '@/shared/utils/ad-readiness';

type Doer = typeof withAntiBotRetry;
type Translate = (key: string) => string;

/**
 * GETs a portal-integration read with auth-only headers (no reCAPTCHA token:
 * the backend only enforces anti-bot on state-changing methods), resolving to
 * `null` on any failure so each caller can fall back to its empty value.
 *
 * @param url - The absolute endpoint URL.
 * @returns The axios response, or null when the request or auth failed.
 */
async function readGet(url: string) {
  try {
    return await axios.get(url, { headers: await buildReadHeaders() });
  } catch {
    return null;
  }
}

/**
 * Builds the base URL for a property's publications endpoints on a given portal.
 *
 * @param agencyId - The agency identifier.
 * @param propertyId - The property identifier.
 * @param portal - The portal slug.
 * @returns The publications base URL.
 */
function publicationsUrl(agencyId: string, propertyId: string, portal: string): string {
  return `${API_BASE}/portal-integration/agencies/${agencyId}/properties/${propertyId}/publications/${portal}`;
}

/**
 * Converts a single raw portal value (a status string or object) into a
 * publication object, or null when it is unusable.
 *
 * @param val - The raw portal value.
 * @returns The normalized publication, or null.
 */
function normalizePortalValue(val: unknown): PortalPublication | null {
  if (typeof val === 'string') return { status: val, post_ids: [], listing_ids: [] };
  if (val && typeof val === 'object') return val as PortalPublication;
  return null;
}

/**
 * Normalizes a raw portals map (whose values may be plain status strings) into
 * a map of publication objects, dropping unusable entries.
 *
 * @param raw - The raw portals map keyed by portal slug.
 * @returns The normalized publications map.
 */
export function normalizePortals(raw: Record<string, unknown>): Record<string, PortalPublication> {
  const portals: Record<string, PortalPublication> = {};
  for (const [key, val] of Object.entries(raw)) {
    const normalized = normalizePortalValue(val);
    if (normalized) portals[key] = normalized;
  }
  return portals;
}

/**
 * Fetches the publish-status portals map for a property, returning an empty map
 * on failure.
 *
 * @param agencyId - The agency identifier.
 * @param propertyId - The property identifier.
 * @returns The normalized portals map.
 */
export async function fetchPortals(
  agencyId: string,
  propertyId: string,
): Promise<Record<string, PortalPublication>> {
  const res = await readGet(
    `${API_BASE}/portal-integration/agencies/${agencyId}/properties/${propertyId}/publish-status`,
  );
  return normalizePortals(res?.data?.portals || {});
}

/**
 * Fetches the agency's connected portals list, returning an empty list on
 * failure.
 *
 * @param agencyId - The agency identifier.
 * @returns The connected portal slugs.
 */
export async function fetchConnectedPortals(agencyId: string): Promise<string[]> {
  const settings = await fetchAgencySettings(agencyId);
  return settings.connectedPortals;
}

/** Subset of the agency Facebook/portal settings needed by publication actions. */
export type AgencyPortalSettings = {
  connectedPortals: string[];
  /** Portals the agency can actually publish to: enabled in settings, Meta-gated and connected -- the same rule as the publish dialog. */
  publishablePortals: string[];
  fbHasPaymentMethod: boolean;
  /** ISO-8601 expiry for the Facebook connection's token, if connected and known. */
  fbTokenExpiresAt: string | null;
  /** ISO-8601 expiry for the native Instagram connection's token, if connected and known. */
  igTokenExpiresAt: string | null;
  /** The agency's Facebook ad account (also bills Instagram ads); null when none is linked. */
  fbAdAccountId: string | null;
  /** Facebook's boost/Lead Ad default daily budget, in cents (matches PublicationRowItem's defaultBoostBudget unit). */
  fbLeadAdBudget: number;
  /** Facebook's boost/Lead Ad default run duration, in days. */
  fbLeadAdDuration: number;
  /** Instagram's boost/Lead Ad default daily budget, in cents (matches PublicationRowItem's defaultBoostBudget unit). */
  igLeadAdBudget: number;
  /** Instagram's boost/Lead Ad default run duration, in days. */
  igLeadAdDuration: number;
  /** What the agency must still fill in before it can create a Lead Ad or a boost, per platform. */
  adReadiness: AdReadiness;
};

/**
 * The portals an agency may publish to, by the publish dialog's own rule: enabled in
 * settings, gated on the Meta connection, and connected. A channel that is connected
 * but switched off is not offered.
 *
 * @param data - The agency settings payload.
 * @returns The publishable portal ids.
 */
export function publishablePortalsFrom(data: Record<string, unknown> | null | undefined): string[] {
  const enabled = Array.isArray(data?.enabled_portals) ? (data.enabled_portals as string[]) : [];
  const connected = Array.isArray(data?.connected_portals) ? (data.connected_portals as string[]) : null;
  return portalsAvailableToPublish(enabled, connected, data?.fb_connected === true, data?.ig_native_connected === true);
}

/**
 * Fetches agency portal-integration settings used by the publications center.
 */
export async function fetchAgencySettings(agencyId: string): Promise<AgencyPortalSettings> {
  const res = await readGet(`${API_BASE}/portal-integration/agencies/${agencyId}/settings`);
  return {
    connectedPortals: res?.data?.connected_portals || [],
    publishablePortals: publishablePortalsFrom(res?.data),
    fbHasPaymentMethod: Boolean(res?.data?.fb_has_payment_method),
    fbTokenExpiresAt: res?.data?.fb_token_expires_at ?? null,
    igTokenExpiresAt: res?.data?.ig_native_token_expires_at ?? null,
    fbAdAccountId: res?.data?.fb_ad_account_id ?? null,
    fbLeadAdBudget: savedBudgetCents(res?.data?.fb_lead_ad_budget),
    fbLeadAdDuration: savedDurationDays(res?.data?.fb_lead_ad_duration),
    igLeadAdBudget: savedBudgetCents(res?.data?.ig_lead_ad_budget),
    igLeadAdDuration: savedDurationDays(res?.data?.ig_lead_ad_duration),
    adReadiness: adReadinessFrom(res?.data),
  };
}

/**
 * Fetches the agency publications catalog index, returning an empty list on failure.
 */
export async function fetchCatalogEntries(agencyId: string): Promise<CatalogPublication[]> {
  const res = await readGet(`${API_BASE}/portal-integration/agencies/${agencyId}/publications`);
  return Array.isArray(res?.data?.entries) ? (res.data.entries as CatalogPublication[]) : [];
}

/**
 * Builds a body map containing whichever of `post_id` / `listing_id` the row
 * carries.
 *
 * @param row - The publication row.
 * @returns The id body map.
 */
export function rowIdBody(row: PublicationRow): Record<string, string> {
  const body: Record<string, string> = {};
  if (row.post_id) body.post_id = row.post_id;
  if (row.listing_id) body.listing_id = row.listing_id;
  return body;
}

/**
 * Deletes a single publication row on its portal.
 *
 * @param agencyId - The agency identifier.
 * @param propertyId - The property identifier.
 * @param portal - The portal slug.
 * @param body - The id body identifying the publication.
 * @param doRequest - Optional request executor (defaults to anti-bot retry).
 * @returns The delete response.
 */
export function deletePublication(
  agencyId: string,
  propertyId: string,
  portal: string,
  body: Record<string, string>,
  doRequest: Doer = withAntiBotRetry,
): Promise<unknown> {
  return doRequest((headers) =>
    axios.delete(publicationsUrl(agencyId, propertyId, portal), { headers, data: body }),
  );
}

/**
 * Fetches a portal post's content, returning null on empty content or failure.
 *
 * @param agencyId - The agency identifier.
 * @param contentPortal - The content portal slug (facebook/instagram).
 * @param postId - The remote post identifier.
 * @returns The post content, or null.
 */
export async function fetchPostContent(
  agencyId: string,
  contentPortal: string,
  postId: string,
): Promise<Record<string, unknown> | null> {
  const res = await withAntiBotRetry((headers) =>
    axios.get(
      `${API_BASE}/portal-integration/agencies/${agencyId}/publications/${contentPortal}/content`,
      { params: { post_id: postId }, headers },
    ),
  );
  const content = res.data;
  if (!content || Object.keys(content).length === 0) return null;
  return content;
}

/**
 * Determines whether the edited image list differs from the originally loaded
 * set, by length or by any positional mismatch.
 *
 * @param edited - The current edited image list.
 * @param original - The originally loaded image list.
 * @returns True when the lists differ.
 */
export function imagesChanged(edited: string[], original: string[]): boolean {
  return edited.length !== original.length || edited.some((img, i) => img !== original[i]);
}

/**
 * Builds the PUT body for saving an edited publication row, including the
 * trimmed custom message and images only when they changed.
 *
 * @param row - The publication row being saved.
 * @param editText - The edited post text.
 * @param editImages - The current edited images.
 * @param editImagesOriginal - The originally loaded images.
 * @returns The save request body.
 */
export function buildSaveBody(
  row: PublicationRow,
  editText: string,
  editImages: string[],
  editImagesOriginal: string[],
): Record<string, unknown> {
  const body: Record<string, unknown> = { ...rowIdBody(row) };
  if (editText.trim()) body.custom_message = editText.trim();
  if (imagesChanged(editImages, editImagesOriginal) && editImages.length > 0) {
    body.images = editImages;
  }
  return body;
}

/**
 * Removes a publication row, reporting success/error via toast and refreshing
 * the list afterwards.
 *
 * @param row - The publication row to remove.
 * @param ctx - Agency/property ids, translator, and refresh callback.
 */
export async function removeRow(
  row: PublicationRow,
  ctx: { agencyId: string; propertyId: string; t: Translate; refresh: () => Promise<void> },
): Promise<void> {
  try {
    await deletePublication(ctx.agencyId, ctx.propertyId, row.portal, rowIdBody(row));
    await ctx.refresh();
    toast.success(ctx.t('publications.toasts.removeSuccess'));
  } catch (error) {
    reportError(error, { message: ctx.t('publications.toasts.removeError') });
  }
}

/**
 * Boosts a Facebook or Instagram post with a daily budget and duration.
 *
 * Both go through the same Facebook ad account server-side; only the
 * endpoint (and which post-id shape it expects) differs by portal.
 */
export async function boostPublication(
  agencyId: string,
  postId: string,
  budgetDailyCents: number,
  durationDays: number,
  portal: 'facebook' | 'instagram' = 'facebook',
): Promise<void> {
  await withAntiBotRetry((headers) =>
    axios.post(
      `${API_BASE}/portal-integration/agencies/${agencyId}/publications/${portal}/boost`,
      {
        post_id: postId,
        budget_daily_cents: budgetDailyCents,
        duration_days: durationDays,
        // Only reached after the user confirmed the spend dialog for exactly these amounts.
        ad_spend_consent: buildAdSpendConsent(budgetDailyCents, durationDays),
      },
      { headers },
    ),
  );
}

/**
 * Retries publishing a single portal for a property.
 */
export async function retryPublication(
  agencyId: string,
  propertyId: string,
  portal: string,
): Promise<void> {
  const res = await withAntiBotRetry((headers) =>
    axios.post(
      `${API_BASE}/portal-integration/agencies/${agencyId}/publish`,
      { property_id: propertyId, portals: [portal] },
      { headers },
    ),
  );
  // A refused portal (e.g. OLX without a plan) still answers 200.
  assertPortalAccepted((res as { data?: unknown } | undefined)?.data, portal);
}

/** Maps a boost route's distinguishable error code to its specific i18n key. */
const BOOST_ERROR_MESSAGE_KEYS: Record<string, string> = {
  fb_not_connected: 'publications.toasts.boostErrorFbNotConnected',
  fb_ad_account_missing: 'publications.toasts.boostErrorFbAdAccountMissing',
  instagram_actor_missing: 'publications.toasts.boostErrorInstagramActorMissing',
};

/**
 * Resolves the boost-failure toast message from the backend's error code
 * when it is one of the known, actionable ones (fb_not_connected,
 * fb_ad_account_missing, instagram_actor_missing) -- falling back to the
 * generic message for anything else (400/502/network failure).
 */
export function resolveBoostErrorMessage(error: unknown, t: Translate): string {
  const code = axios.isAxiosError(error) ? error.response?.data?.error : undefined;
  const key = (code && BOOST_ERROR_MESSAGE_KEYS[code]) || 'publications.toasts.boostError';
  return t(key);
}

/**
 * Whether a failed boost carries one of the backend's specific, short
 * precondition codes (not connected / no ad account / no Instagram actor) --
 * those keep their own focused toast. Anything else is Meta rejecting the
 * campaign itself, which needs the full "why and what to do" dialog.
 */
export function isKnownBoostErrorCode(error: unknown): boolean {
  const code = boostErrorCode(error);
  return Boolean(code && code in BOOST_ERROR_MESSAGE_KEYS);
}

/**
 * Runs a boost request with feedback and list refresh. A failure that is not
 * one of the known precondition codes opens the ad-error dialog (when
 * `showAdError` is provided) instead of a toast.
 */
export async function boostRow(
  row: PublicationRow,
  budgetDailyCents: number,
  durationDays: number,
  ctx: {
    agencyId: string;
    t: Translate;
    refresh: () => Promise<void>;
    showAdError?: (error: AdErrorInfo) => void;
  },
): Promise<void> {
  if (!row.post_id) return;
  try {
    await boostPublication(
      ctx.agencyId, row.post_id, budgetDailyCents, durationDays,
      row.portal === 'instagram' ? 'instagram' : 'facebook',
    );
    await ctx.refresh();
    toast.success(ctx.t('publications.toasts.boostSuccess'));
  } catch (error) {
    if (ctx.showAdError && !isKnownBoostErrorCode(error)) {
      ctx.showAdError({ kind: 'boost', detail: boostErrorDetail(error) });
      return;
    }
    reportError(error, { message: resolveBoostErrorMessage(error, ctx.t) });
  }
}

/**
 * Runs a retry-publish request with toast feedback and list refresh.
 */
export async function retryRow(
  row: PublicationRow,
  ctx: {
    agencyId: string;
    propertyId: string;
    propertyImages: string[];
    t: Translate;
    refresh: () => Promise<void>;
  },
): Promise<void> {
  if (row.portal === 'instagram' && ctx.propertyImages.length === 0) {
    toast.error(ctx.t('publish.instagramRequiresImages'));
    return;
  }
  try {
    await retryPublication(ctx.agencyId, ctx.propertyId, row.portal);
    await ctx.refresh();
    toast.success(ctx.t('publications.toasts.retrySuccess'));
  } catch (error) {
    reportError(error, { message: refusalMessage(error, 'publications.toasts.retryError', ctx.t) });
  }
}

/**
 * Persists an edited publication row via PUT.
 *
 * @param agencyId - The agency identifier.
 * @param propertyId - The property identifier.
 * @param portal - The portal slug.
 * @param body - The save request body.
 * @returns The save response.
 */
export function saveRow(
  agencyId: string,
  propertyId: string,
  portal: string,
  body: Record<string, unknown>,
): Promise<unknown> {
  return withAntiBotRetry((headers) =>
    axios.put(publicationsUrl(agencyId, propertyId, portal), body, { headers }),
  );
}

/**
 * Determines whether a facebook post has no content (and is therefore stale),
 * returning false on any request failure.
 *
 * @param agencyId - The agency identifier.
 * @param postId - The remote post identifier.
 * @param doRequest - Request executor.
 * @returns True when the post content is empty.
 */
async function isPostContentEmpty(agencyId: string, postId: string, doRequest: Doer): Promise<boolean> {
  try {
    const res = await doRequest((headers) =>
      axios.get(`${API_BASE}/portal-integration/agencies/${agencyId}/publications/facebook/content`, {
        params: { post_id: postId },
        headers,
      }),
    );
    const content = res.data;
    return !content || Object.keys(content).length === 0;
  } catch {
    return false;
  }
}

/**
 * Returns the post ids for a publication, falling back to the single-id field
 * when the plural list is absent.
 *
 * @param pub - The portal publication.
 * @returns The post ids.
 */
function pubPostIds(pub: PortalPublication): string[] {
  return pub.post_ids || (pub.post_id ? [pub.post_id] : []);
}

type StaleEntry = { portal: string; post_id?: string };

/**
 * Collects stale facebook post ids by probing each post's content for
 * emptiness.
 *
 * @param agencyId - The agency identifier.
 * @param pub - The facebook publication.
 * @param doRequest - Request executor.
 * @returns The stale post entries.
 */
async function collectStaleFacebook(agencyId: string, pub: PortalPublication, doRequest: Doer): Promise<StaleEntry[]> {
  const postIds = pubPostIds(pub);
  const checks = await Promise.all(postIds.map((pid) => isPostContentEmpty(agencyId, pid, doRequest)));
  return postIds.filter((_, i) => checks[i]).map((pid) => ({ portal: 'facebook', post_id: pid }));
}

/**
 * Determines whether a portal is a meta (facebook/instagram) portal.
 *
 * @param portal - The portal slug.
 * @returns True for facebook or instagram.
 */
function isMetaPortal(portal: string): boolean {
  return portal === 'facebook' || portal === 'instagram';
}

/**
 * Identifies stale (published-but-empty) meta publications for a single portal
 * entry: flags published entries with no posts, and probes facebook posts.
 *
 * @param agencyId - The agency identifier.
 * @param portal - The portal slug.
 * @param pub - The portal publication.
 * @param doRequest - Request executor.
 * @returns The stale entries for this portal.
 */
async function collectStaleForPortal(agencyId: string, portal: string, pub: PortalPublication, doRequest: Doer): Promise<StaleEntry[]> {
  if (!isMetaPortal(portal)) return [];
  const hasNoPosts = pubPostIds(pub).length === 0;
  if (hasNoPosts && pub.status === 'published') return [{ portal }];
  return portal === 'facebook' ? collectStaleFacebook(agencyId, pub, doRequest) : [];
}

/**
 * Deletes any stale meta publications discovered across the portals map.
 *
 * @param agencyId - The agency identifier.
 * @param propertyId - The property identifier.
 * @param portals - The portals map to scan.
 * @param doRequest - Optional request executor (defaults to anti-bot retry).
 * @returns True if at least one stale publication was purged.
 */
export async function purgeStalePosts(
  agencyId: string,
  propertyId: string,
  portals: Record<string, PortalPublication>,
  doRequest: Doer = withAntiBotRetry,
): Promise<boolean> {
  const nested = await Promise.all(
    Object.entries(portals).map(([portal, pub]) => collectStaleForPortal(agencyId, portal, pub, doRequest)),
  );
  const stale = nested.flat();
  if (stale.length === 0) return false;
  await Promise.allSettled(
    stale.map(({ portal, post_id }) =>
      deletePublication(agencyId, propertyId, portal, post_id ? { post_id } : {}, doRequest),
    ),
  );
  return true;
}
